import { useEffect, useState } from "react";
import google from "../../images/google.png";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/authcontext";
import { toast } from "../../utils/appToast";

export const Login = () => {
  const navigate = useNavigate();
  const { signInWithGoogle, signup, signin, resetPassword, user, loading } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isChecked, setIsChecked] = useState(false);
  const [mayuscula, setMayuscula] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleChange = ({ target: { name, value } }) =>
    setForm((f) => ({ ...f, [name]: value }));

  const togglePassword = () =>
    setPasswordType((t) => (t === "password" ? "text" : "password"));
  const isForgotPasswordMode = mode === "forgotPassword";

  const passwordsMatch = form.password === form.confirmPassword && form.confirmPassword !== "";
  const showPasswordError = mode === "register" && form.confirmPassword !== "" && form.password !== form.confirmPassword;

  const handleModeChange = (newMode, options = {}) => {
    const nextEmail = options.keepEmail ? form.email : "";
    setMode(newMode);
    setForm({
      firstName: "",
      lastName: "",
      email: nextEmail,
      password: "",
      confirmPassword: "",
    });
    setIsChecked(false);
  };

  useEffect(() => {
    const handler = (e) => setMayuscula(e.getModifierState?.("CapsLock"));
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isForgotPasswordMode) {
      await handleForgotPassword();
      return;
    }
    if (!isChecked) return toast.error("Debe aceptar los términos y condiciones");
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();

    if (mode === "register" && (!firstName || !lastName)) {
      return toast.error("Debes completar nombre y apellido para registrarte.");
    }

    if (mode === "register" && !passwordsMatch) {
      return toast.error("Las contraseñas no coinciden. Deben ser iguales.");
    }

    try {
      if (mode === "login") {
        await signin(form.email, form.password);
        navigate("/home", { replace: true });
      } else {
        const registerResult = await signup(form.email, form.password, { firstName, lastName });
        navigate("/verificacion-email", {
          replace: true,
          state: { email: registerResult?.email || form.email },
        });
      }
    } catch (error) {
      toast.error(mapAuthError(error));
    }
  };

  const handleSigninGoogle = async () => {
    if (isGoogleLoading) return;
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      const code = e?.code || "";
      if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        toast.error(mapAuthError(e));
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = form.email.trim();

    if (!email) {
      toast.error("Ingresá tu email para recuperar la contraseña.");
      return;
    }

    try {
      await resetPassword(email);
      toast.success("Te enviamos un correo para restablecer tu contraseña.");
      handleModeChange("login", { keepEmail: true });
    } catch (error) {
      toast.error(mapAuthError(error));
    }
  };

  useEffect(() => {
    if (!loading && user) {
      navigate("/home", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-peabodyLight/30 font-sans auth-shell">
      <div className="w-full max-w-md">
        <div className="bg-white/70 backdrop-blur-md shadow-xl border border-white/50 rounded-2xl overflow-hidden transition-all duration-300">
          <div className="p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-center text-peabodyDark mb-8">
              {mode === "login"
                ? "Iniciá sesión"
                : mode === "register"
                  ? "Registrate"
                  : "Recuperar contraseña"}
            </h2>

            {!isForgotPasswordMode && (
              <>
                <button
                  onClick={handleSigninGoogle}
                  className="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl shadow-sm transition-all duration-200 focus:ring-2 focus:ring-offset-1 focus:ring-peabodyBlue disabled:opacity-50"
                  type="button"
                  disabled={isGoogleLoading}
                >
                  <img src={google} width="20" height="20" alt="Google logo" />
                  {isGoogleLoading ? "Conectando..." : "Continuar con Google"}
                </button>

                <div className="flex items-center my-6">
                  <div className="flex-grow border-t border-gray-200"></div>
                  <span className="px-3 text-sm text-gray-400">o con tu correo</span>
                  <div className="flex-grow border-t border-gray-200"></div>
                </div>
              </>
            )}

            <form onSubmit={onSubmit} noValidate className="space-y-4">
              {mode === "register" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-peabodyBlue focus:border-transparent outline-none transition-all duration-200"
                    placeholder="Nombre"
                    autoComplete="given-name"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-peabodyBlue focus:border-transparent outline-none transition-all duration-200"
                    placeholder="Apellido"
                    autoComplete="family-name"
                    required
                  />
                </div>
              )}

              <div>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-peabodyBlue focus:border-transparent outline-none transition-all duration-200"
                  placeholder="Email"
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>

              {!isForgotPasswordMode && (
                <div className="relative">
                  <input
                    type={passwordType}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-50/50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-peabodyBlue focus:border-transparent outline-none transition-all duration-200 pr-20"
                    placeholder="Contraseña"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-peabodyBlue hover:text-peabodyDark focus:outline-none"
                    onClick={togglePassword}
                    aria-label="Mostrar/ocultar contraseña"
                  >
                    {passwordType === "password" ? "Mostrar" : "Ocultar"}
                  </button>
                </div>
              )}

              {mode === "register" && (
                <div>
                  <div className="relative">
                    <input
                      type={passwordType}
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-gray-50/50 border rounded-xl focus:ring-2 focus:border-transparent outline-none transition-all duration-200 pr-20 ${
                        showPasswordError
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:ring-peabodyBlue"
                      }`}
                      placeholder="Repetir contraseña"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-peabodyBlue hover:text-peabodyDark focus:outline-none"
                      onClick={togglePassword}
                      aria-label="Mostrar/ocultar contraseña"
                    >
                      {passwordType === "password" ? "Mostrar" : "Ocultar"}
                    </button>
                  </div>
                  {showPasswordError && (
                    <p className="text-xs text-red-500 font-medium mt-1.5 px-1">
                      Las contraseñas no coinciden. Deben ser iguales.
                    </p>
                  )}
                  {passwordsMatch && form.confirmPassword !== "" && (
                    <p className="text-xs text-green-600 font-medium mt-1.5 px-1">
                      ✓ Las contraseñas coinciden
                    </p>
                  )}
                </div>
              )}

              {!isForgotPasswordMode && (
                <>
                  <p className="min-h-[1.25rem] text-xs text-amber-600 font-medium px-1">
                    {mayuscula ? "Bloq Mayus activado" : ""}
                  </p>

                  <div className="flex items-start gap-2 mt-1 px-1">
                    <input
                      className="mt-1 w-4 h-4 text-peabodyBlue border-gray-300 rounded focus:ring-peabodyBlue cursor-pointer"
                      type="checkbox"
                      id="tyc"
                      checked={isChecked}
                      onChange={(e) => setIsChecked(e.target.checked)}
                    />
                    <label htmlFor="tyc" className="text-sm text-gray-600 cursor-pointer select-none">
                      He leído y acepto las{" "}
                      <Link to="/terminosycondiciones" className="text-peabodyBlue hover:text-peabodyDark underline underline-offset-2">Políticas de Privacidad</Link> y los{" "}
                      <Link to="/terminosycondiciones" className="text-peabodyBlue hover:text-peabodyDark underline underline-offset-2">Términos y Condiciones</Link>
                    </label>
                  </div>
                </>
              )}

              <button
                className="w-full py-3.5 mt-2 bg-gradient-to-r from-peabodyBlue to-peabodyDark hover:from-peabodyDark hover:to-[#1a1a1a] text-white font-semibold rounded-xl shadow-md shadow-peabodyBlue/30 hover:shadow-lg hover:shadow-peabodyBlue/40 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={!isForgotPasswordMode && !isChecked}
              >
                {mode === "login"
                  ? "Iniciar sesión"
                  : mode === "register"
                    ? "Registrarme"
                    : "Enviar correo de recuperación"}
              </button>
            </form>

            <div className="text-center mt-8 space-y-2">
              {mode === "login" ? (
                <p className="text-sm text-gray-600">
                  No tenés cuenta?{" "}
                  <button
                    type="button"
                    className="font-semibold text-peabodyBlue hover:text-peabodyDark transition-colors"
                    onClick={() => handleModeChange("register")}
                  >
                    Registrarme
                  </button>
                </p>
              ) : mode === "register" ? (
                <p className="text-sm text-gray-600">
                  Ya tenés cuenta?{" "}
                  <button
                    type="button"
                    className="font-semibold text-peabodyBlue hover:text-peabodyDark transition-colors"
                    onClick={() => handleModeChange("login")}
                  >
                    Iniciar sesión
                  </button>
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  Te enviaremos un link de recuperación al correo que indiques.
                </p>
              )}

              {mode === "login" && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-gray-500 hover:text-peabodyBlue transition-colors"
                    onClick={() => handleModeChange("forgotPassword", { keepEmail: true })}
                  >
                    Olvidaste tu contraseña?
                  </button>
                </div>
              )}

              {mode === "forgotPassword" && (
                <div className="mt-2">
                  <button
                    type="button"
                    className="text-xs font-medium text-gray-500 hover:text-peabodyBlue transition-colors"
                    onClick={() => handleModeChange("login", { keepEmail: true })}
                  >
                    Volver a iniciar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="pb-4" style={{ paddingBottom: "env(safe-area-inset-bottom, 1rem)" }} />
      </div>
    </div>
  );
};

function mapAuthError(error) {
  const code = error?.code || "";
  const map = {
    "auth/user-not-found": "No estás registrado. Debes registrarte para iniciar sesión.",
    "auth/wrong-password": "El email o la contraseña es inválido.",
    "auth/invalid-credential": "El email o la contraseña es inválido.",
    "auth/invalid-login-credentials": "El email o la contraseña es inválido.",
    "auth/invalid-email": "Email inválido.",
    "auth/email-already-in-use": "El email ya está en uso.",
    "auth/weak-password": "La contraseña es demasiado débil.",
    "auth/popup-closed-by-user": "Se cerró la ventana de Google.",
    "auth/cancelled-popup-request": "Se canceló el intento de inicio con Google.",
    "auth/popup-blocked": "El navegador bloqueó la ventana de Google. Habilita popups para este sitio.",
    "auth/unauthorized-domain": "Este dominio no está autorizado en Firebase Authentication.",
    "auth/too-many-requests": "Demasiados intentos. Intenta nuevamente en unos minutos.",
    "auth/email-not-verified": "Tu email no está verificado. Revisa tu correo para poder ingresar.",
    "auth/missing-email": "Debes ingresar un email.",
  };

  return map[code] || "Ocurrió un error al iniciar sesión. Intenta nuevamente.";
}


